<div class="interaction-list p-4">
  <!-- Initial Loading State (only when no user data yet) -->
  <div *ngIf="loading && !currentUser" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <p-progressSpinner styleClass="w-16 h-16" strokeWidth="3" animationDuration="1s"></p-progressSpinner>
      <p class="text-gray-600 mt-4 text-lg" i18n>Loading user profile...</p>
    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="loadingError && !loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <i class="pi pi-exclamation-triangle text-red-500 text-6xl mb-4"></i>
      <h2 class="text-xl font-semibold text-gray-800 mb-2" i18n>Error Loading Interactions</h2>
      <p class="text-gray-600 mb-4">{{ loadingError }}</p>
      <p-button appGlobalLoadingDisable 
        label="Try Again" 
        icon="pi pi-refresh" 
        (onClick)="loadCurrentUser()"
        severity="secondary"
        i18n-label>
      </p-button>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!loadingError">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2" i18n>My Interactions</h1>
        <p class="text-gray-600" i18n>Track and manage all your customer interactions</p>
        <div class="text-sm text-blue-600 mt-1" *ngIf="currentUser" i18n>
          <i class="pi pi-user mr-1"></i>
          {{ currentUser.firstName }} {{ currentUser.lastName }} - {{ interactions.length }} total interactions
        </div>
      </div>
      <div class="flex gap-3">
        <p-button appGlobalLoadingDisable 
          label="Schedule New" 
          icon="pi pi-calendar-plus" 
          (onClick)="router.navigate(['/admin/shedule-interaction'])"
          [pTooltip]="'Schedule a new customer interaction'"
          i18n-label
          i18n-pTooltip>
        </p-button>
        <p-button appGlobalLoadingDisable 
          label="Log Interaction" 
          icon="pi pi-plus" 
          (onClick)="router.navigate(['/admin/log-interaction'])"
          [pTooltip]="'Log a completed interaction'"
          i18n-label
          i18n-pTooltip>
        </p-button>
      </div>
    </div>

  <p-card>
    <ng-template pTemplate="header">
      <div class="p-4">
        <div class="flex flex-wrap gap-4 items-center">
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-700" i18n>Search:</label>
            <input 
              type="text" 
              pInputText 
              [(ngModel)]="searchTerm" 
              (input)="onSearchChange()"
              placeholder="Search by customer, type, status, or notes..." i18n-placeholder
              class="w-64"
              [pTooltip]="'Search across customer names, interaction types, statuses, and notes'"
              i18n-pTooltip>
          </div>
         
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-700" i18n>Status:</label>
            <p-select 
              [(ngModel)]="statusFilter" 
              [options]="statusOptions" 
              optionLabel="label" 
              optionValue="value"
              (onChange)="onStatusFilterChange()"
              placeholder="Filter by status"
              i18n-placeholder
              [style]="{'min-width': '150px'}"
              [pTooltip]="'Filter interactions by status'"
              i18n-pTooltip>
            </p-select>
          </div>
        </div>
      </div>
    </ng-template>

    <div class="overflow-x-auto">
      <p-table 
        [value]="filteredInteractions" 
        [loading]="loading"
        [tableStyle]="{'min-width': '70rem'}"
        [paginator]="true"
        [rows]="20"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} interactions" i18n-currentPageReportTemplate
        [globalFilterFields]="['customerName', 'type', 'status', 'notes']">
        
        <ng-template pTemplate="header">
          <tr>
            <th i18n>Customer</th>
            <th i18n>Type</th>
            <th i18n>Status</th>
            <th i18n>Date & Time</th>
            <th i18n>Reservation</th>
            <th i18n>Notes</th>
            <th i18n>Actions</th>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="body" let-interaction>
          <tr>
            <td>
              <div class="flex items-center gap-2">
                <i class="pi pi-building text-gray-500"></i>
                <div>
                  <div class="font-medium cursor-pointer hover:text-blue-600" 
                       (click)="onViewCustomer(interaction)"
                       [pTooltip]="'Click to view customer details'"
                       i18n-pTooltip>
                    {{ getCustomerName(interaction) }}
                  </div>
                  <div class="text-sm text-gray-500">{{ getCustomerLocation(interaction) }}</div>
                </div>
              </div>
            </td>
            <td>
              <p-tag 
                [value]="mapInteractionType(interaction.interactionType)" 
                [severity]="getTypeSeverity(mapInteractionType(interaction.interactionType))"
                [pTooltip]="'Interaction type'"
                i18n-pTooltip>
              </p-tag>
            </td>
            <td>
              <p-tag 
                [value]="mapInteractionStatus(interaction.interactionStatus)" 
                [severity]="getStatusSeverity(mapInteractionStatus(interaction.interactionStatus))"
                [pTooltip]="'Current status'"
                i18n-pTooltip>
              </p-tag>
            </td>
            <td>
              <div>
                <div class="text-sm font-medium">{{ formatDate(interaction.interactionDate) }}</div>
                <div class="text-xs text-gray-500">{{ formatDateTime(interaction.interactionDate) }}</div>
                <div class="text-xs text-blue-600" *ngIf="interaction.interactionTime">
                  {{ interaction.interactionTime }}
                </div>
              </div>
            </td>
            <td>
              <div>
                <span class="text-sm cursor-pointer hover:text-blue-600" 
                      (click)="onViewReservation(interaction)"
                      [pTooltip]="'Click to view reservation details'"
                      i18n-pTooltip
                      i18n>
                  View Reservation
                </span>
                <div class="text-xs text-gray-500">{{ getReservationStatus(interaction) }}</div>
              </div>
            </td>
            <td>
              <div class="text-sm text-gray-600 max-w-xs truncate" 
                   [title]="interaction.interactionNotes || 'No notes available'"
                   i18n-title>
                {{ interaction.interactionNotes || 'No notes' }}
              </div>
            </td>
            <td>
              <div class="flex gap-2">
                <p-button appGlobalLoadingDisable 
                  label="View" 
                  [text]="true" 
                  size="small"
                  (onClick)="onViewInteraction(interaction._id)"
                  [pTooltip]="'View interaction details'"
                  i18n-label
                  i18n-pTooltip>
                </p-button>
                <p-button 
                  label="Edit" 
                  [text]="true" 
                  size="small"
                  (onClick)="onViewInteraction(interaction._id)"
                  [disabled]="mapInteractionStatus(interaction.interactionStatus) === 'Completed'"
                  [pTooltip]="mapInteractionStatus(interaction.interactionStatus) === 'Completed' ? 'Cannot edit completed interactions' : 'Edit interaction details'"
                  i18n-label
                  i18n-pTooltip>
                </p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="text-center py-8">
              <div class="flex flex-col items-center gap-4">
                <i class="pi pi-calendar-times text-gray-400 text-6xl"></i>
                <div>
                  <h3 class="text-lg font-medium text-gray-900 mb-2" i18n>
                    {{ filteredInteractions.length === 0 && interactions.length > 0 ? 'No interactions match your filters' : 'No interactions found' }}
                  </h3>
                  <p class="text-gray-600" i18n>
                    {{ filteredInteractions.length === 0 && interactions.length > 0 ? 'Try adjusting your search or filter criteria' : 'Interactions will appear here once you start scheduling and logging them' }}
                  </p>
                  <div class="flex gap-2 mt-4" *ngIf="filteredInteractions.length === 0 && interactions.length === 0">
                    <p-button appGlobalLoadingDisable 
                      label="Schedule Interaction" 
                      icon="pi pi-calendar-plus" 
                      [outlined]="true"
                      (onClick)="router.navigate(['/admin/shedule-interaction'])"
                      i18n-label>
                    </p-button>
                    <p-button appGlobalLoadingDisable 
                      label="Log Interaction" 
                      icon="pi pi-plus" 
                      [outlined]="true"
                      (onClick)="router.navigate(['/admin/log-interaction'])"
                      i18n-label>
                    </p-button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </p-card>
  </div> <!-- End of main content -->
</div>




